import { assertPrivateSession, getPrivateSessionState } from "@/lib/api/private-session";
import { apiFetchClient } from "@/lib/api/client";

type JsonRecord = Record<string, unknown>;

function isJsonObject(value: unknown): value is JsonRecord {
  return typeof value === "object" && value !== null;
}

function hasJsonResponse(response: Response) {
  const contentType = response.headers.get("content-type");
  return contentType?.includes("json") ?? false;
}

async function parseResponsePayload(response: Response): Promise<unknown> {
  if (response.status === 204 || response.status === 205) {
    return undefined;
  }

  if (!hasJsonResponse(response)) {
    const text = await response.text();
    return text.length > 0 ? text : undefined;
  }

  return response.json();
}

function getErrorCode(payload: unknown): string | undefined {
  if (!isJsonObject(payload)) {
    return undefined;
  }

  const code = payload.code;
  return typeof code === "string" && code.length > 0 ? code : undefined;
}

function getErrorMessageFromPayload(payload: unknown): string | undefined {
  if (!isJsonObject(payload)) {
    return undefined;
  }

  const message = payload.message;
  if (typeof message === "string" && message.length > 0) {
    return message;
  }

  if (Array.isArray(message)) {
    const normalizedMessages = message.filter(
      (item): item is string => typeof item === "string" && item.length > 0,
    );

    if (normalizedMessages.length > 0) {
      return normalizedMessages.join(", ");
    }
  }

  return undefined;
}

export class ApiJsonClientError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly payload?: unknown;

  constructor(params: {
    message: string;
    status: number;
    code?: string;
    payload?: unknown;
  }) {
    super(params.message);
    this.name = "ApiJsonClientError";
    this.status = params.status;
    this.code = params.code;
    this.payload = params.payload;
  }
}

async function parseApiError(
  response: Response,
  fallbackMessage: string,
): Promise<ApiJsonClientError> {
  let payload: unknown;

  try {
    payload = await parseResponsePayload(response);
  } catch {
    payload = undefined;
  }

  // The body was already consumed by parseResponsePayload above — derive the
  // message from the parsed payload (re-reading the response would throw).
  const message =
    getErrorMessageFromPayload(payload) ||
    response.statusText ||
    fallbackMessage;

  return new ApiJsonClientError({
    message,
    status: response.status,
    code: getErrorCode(payload),
    payload,
  });
}

export async function apiJsonClient<TResponse = unknown>(
  path: string,
  options: RequestInit = {},
): Promise<TResponse> {
  const privateRequest = path.replace(/^\/+/, "").startsWith("seller/") &&
    !/^seller\/auth\/(?:login|logout|switch-store)(?:\?|$)/.test(path.replace(/^\/+/, ""));
  const generation = getPrivateSessionState().generation;
  const response = await apiFetchClient(path, options);

  if (!response.ok) {
    const error = await parseApiError(response, "Request failed");
    if (privateRequest) assertPrivateSession(generation);
    throw error;
  }

  let payload: unknown;
  try {
    payload = await parseResponsePayload(response);
  } catch (error) {
    if (privateRequest) assertPrivateSession(generation);
    throw error;
  }
  if (privateRequest) assertPrivateSession(generation);
  return payload as TResponse;
}
